import swal from "sweetalert";
import { ReportResolveType } from "shared/Ticket";

/**
 * Thin wrappers over sweetalert so dialogs are configured in one place.
 * Callers pass already-translated strings (this.$t / i18n t); look and feel
 * is unchanged from the hand-written swal({...}) calls they replace.
 */

export type DialogKind = "success" | "error" | "warning" | "info";

export interface ConfirmOptions {
  title: string;
  text?: string;
  /** Red confirm button (swal dangerMode). */
  danger?: boolean;
  confirmText?: string;
  cancelText?: string;
  /** Defaults to "warning". */
  icon?: DialogKind | "question";
}

export interface AlertOptions {
  title?: string;
  /** swal dangerMode (red confirm button). */
  danger?: boolean;
  /** Extra swal class; the app default is "alert". Pass null for none. */
  className?: string | null;
}

/** Ask for confirmation. Resolves true only when the confirm button is used. */
export async function confirm(options: ConfirmOptions): Promise<boolean> {
  const cancel: any = { value: false, visible: true, closeModal: true };
  const ok: any = { value: true, visible: true, closeModal: true };
  if (options.cancelText !== undefined) cancel.text = options.cancelText;
  if (options.confirmText !== undefined) ok.text = options.confirmText;
  const config: any = {
    title: options.title,
    icon: options.icon ?? "warning",
    className: "alert",
    buttons: { cancel, confirm: ok },
  };
  if (options.text !== undefined) config.text = options.text;
  if (options.danger) config.dangerMode = true;
  const result: any = await swal(config);
  return !!result && !result.isDismissed;
}

function showAlert(kind: DialogKind, message: string, options: AlertOptions = {}) {
  const className = options.className === undefined ? "alert" : options.className;
  const config: any = { text: message, icon: kind };
  if (options.title !== undefined) config.title = options.title;
  if (className) config.className = className;
  if (options.danger) config.dangerMode = true;
  return swal(config);
}

export function alertError(message: string, options: AlertOptions = {}) {
  return showAlert("error", message, { danger: true, ...options });
}

export function alertWarning(message: string, options: AlertOptions = {}) {
  return showAlert("warning", message, options);
}

export function alertSuccess(message: string, options: AlertOptions = {}) {
  return showAlert("success", message, options);
}

export interface ToastOptions {
  title?: string;
  /** Extra swal class; the app default is "alert". Pass null for none. */
  className?: string | null;
}

/** Transient notice that closes itself (no buttons). */
export function toast(message: string, kind: DialogKind = "info", timerMs = 2500, options: ToastOptions = {}) {
  const className = options.className === undefined ? "alert" : options.className;
  const config: any = { icon: kind, buttons: false as any, timer: timerMs };
  if (options.title !== undefined) config.title = options.title;
  if (message) config.text = message;
  if (className) config.className = className;
  return swal(config);
}

/**
 * Report close dialog: cancel / invalid / valid. Resolves with the chosen
 * ReportResolveType (UNRESOLVED when cancelled).
 */
export async function chooseReportResolution(t: (key: string) => string): Promise<ReportResolveType> {
  return (await swal({
    icon: "info",
    title: t("report.resolutionTitle"),
    text: t("report.resolutionInfo"),
    className: "alert",
    buttons: {
      cancel: { text: t("cancel"), value: ReportResolveType.UNRESOLVED, visible: true, closeModal: true },
      invalidReport: {
        text: t("report.invalid"),
        value: ReportResolveType.INVALID,
        visible: true,
        className: "invalid-report-button",
        closeModal: true,
      },
      confirm: {
        text: t("report.valid"),
        value: ReportResolveType.VALID,
        visible: true,
        className: "valid-report-button",
        closeModal: true,
      },
    },
  } as any)) as unknown as ReportResolveType;
}

export function useDialogs() {
  return { confirm, alertError, alertWarning, alertSuccess, toast, chooseReportResolution };
}
